import { Injectable } from '@angular/core';
import { EnvironmentService } from '../environment/environment.service';
import ProjectCreditOutlet from "../interfaces/project-credit-outlet";
import ProjectCredit from "../interfaces/project-credit";
import taxCreditDesignation from "../interfaces/tax-credit-designation";
import Address from "../interfaces/address";

@Injectable({
  providedIn: 'root',
})
export class DataFormatService {
  constructor(private env: EnvironmentService) {}

  //Pass in a valid GraphQL object with an id and it will
  //return a formatted link based on the environment.
  public formatLink(obj: any, subroute?: string): string {
    //Sets the default link to Jupiter's url.
    //This can be overwritten and updated as NOVA expands.
    let link = this.env.jupiter_url;
    let nova_link = this.env.nova_url;

    //If no subroute is provided, it'll set 'view' as the default
    if (!subroute) {
      subroute = 'view';
    }
    if (obj.hasOwnProperty('__typename') && obj.hasOwnProperty('id')) {
      let sublink = '/' + subroute + '/' + obj.id;
      switch (obj.__typename) {
        case 'Project':
          nova_link += '/deal/' + obj.id;
          return nova_link;
        case 'ProjectDetail':
          // ----- This is temporarily for Dispositions -----
          link += '/deal/dispositions/' + obj.project_id;
          break;
        case 'Organization':
          nova_link += '/contacts/organizations/' + obj.id;
          return nova_link
        case 'Sponsor':
          nova_link += '/contacts/sponsors/' + obj.id;
          return nova_link
        case 'Person':
          nova_link += '/contacts/people/' + obj.id;
          return nova_link
        case 'Fund':
          nova_link += '/fund/' + obj.id;
          return nova_link;
        case 'CertificatedOutlet':
          nova_link += '/certificated_outlet/' + obj.id;
          return nova_link;
        case 'CompanyEvent':
          nova_link += '/company_events/' + obj.id
          return nova_link
      }
      return link;
    } else {
      return '';
    }
  }

  public snakeCaseToCamelCase(input: string): string {
    return input
      .toLowerCase()
      .replace(/([-_][a-z])/g, (group) =>
        group.toUpperCase().replace('-', '').replace('_', '')
      );
  }
  public yesNoFormat(value: boolean | null): string {
    return value ? 'Yes' : value === null ? 'N/A' : 'No';
  }

  public phoneFormat(areaCode:string|null, number: string){
    if(areaCode){
      return areaCode + "-" + number
    }
    return number
  }

  public formatWeblink(parsedLink:any){
    let protocol = parsedLink.protocol
    let subdomain = parsedLink.subdomain
    let domain = parsedLink.domain
    let tld = parsedLink.tld
    let path = parsedLink.path
    let parameters = parsedLink.parameters
    let resolveUrl:string;
    resolveUrl = protocol==1 ? 'https' : 'http';
    resolveUrl += "://";
    resolveUrl += subdomain ? subdomain + "." : "";
    resolveUrl += domain ? domain + "." : "";
    resolveUrl += tld ? tld : "";
    resolveUrl += path ? path : "";
    resolveUrl += parameters ? parameters : "";
    return resolveUrl
  }

  public formatCreditTypes(
    obj: any,
    include_not_crea_syndicated: boolean
  ): any {
    let types: any = [];
    if (obj !== null && obj.length > 0) {
      for (let i = 0; i < obj.length; i++) {
        let credit: {name:string, equity:number|null} | null = null;
        if (
          obj[i].hasOwnProperty('__typename') &&
          obj[i].__typename === 'ProjectCredit' &&
          (obj[i].crea_syndicated === true || include_not_crea_syndicated)
        ) {
          credit = {
             name:this.getCreditName(obj[i]),
             equity: obj[i].amount ?? null
          }
        }

        if (credit) {
          types.push(credit);
        }
      }
    }
    return types.sort();
  }

  public formatAddress(address:Address){
    let addressString = address.address1
    if(address.address2){
      addressString = addressString + ", "  + address.address2
    }
    addressString = addressString + ", "  + address.zipCode.city + ", " + address.zipCode.state.abbreviation + " " + address.zipCode.zipcode

    return addressString
  }

  private getCreditName(credit: ProjectCredit){
    let name = '';
    if (credit.taxCreditType?.level) {
      name +=
        credit.taxCreditType.level + ' ' + credit.taxCreditType.name;
    }
    if (credit.taxCreditDesignation?.name) {
      name += ' - ' + credit.taxCreditDesignation.name;
    }
    if(credit.certificated){
      name += ' (Cert.)';
    }

    return name;
  }

  private initializeOutlet(outlet: ProjectCreditOutlet) {

    if(outlet.fund){
      return {
        id:outlet.fund.id,
        name: outlet.fund.name,
        link: this.formatLink(outlet.fund),
        credits: []
      }
    } else if(outlet.certificatedOutlet) {
      return {
        id:outlet.certificatedOutlet.id,
        name: outlet.certificatedOutlet.organization.name,
        link:this.formatLink(outlet.certificatedOutlet),
        credits: []
      }
    }
    return false;

  }
  private outletGrossEquity(outlet: ProjectCreditOutlet, credit: ProjectCredit): {equity:number|null, is_calculated: boolean, is_manual:boolean } {
    let data:{equity:number|null, is_calculated:boolean, is_manual:boolean} = {
      equity:null,
      is_calculated:false,
      is_manual: false,
    }

    if(outlet.gross_equity){
      data.equity = outlet.gross_equity
    }else if(outlet.estimated_gross_equity){
      data.equity = outlet.estimated_gross_equity
      data.is_manual = true;
    }else{
      //@todo add the constants defined on backend once those are merged into develop
      let load = credit.certificated ? 1.04 : 1.08;
      data.is_calculated = true;
      data.equity = outlet.ownership !== null && credit.equity_amount !== null ?  credit.equity_amount * load * outlet.ownership /100 : null;
    }

    return data;
  }
  public formatCreditOutlets(credits?: ProjectCredit[]): any {
    let outlets: any = [];
    if (credits && credits.length > 0) {
      for (let i = 0; i < credits.length; i++) {
        let credit = credits[i];
        if (credit.projectCreditOutlets) {
          for (let j = 0; j < credit.projectCreditOutlets.length; j++) {
            let creditOutlet = credit.projectCreditOutlets[j];
            let dataIndex = outlets.findIndex((outlet:ProjectCreditOutlet)=>outlet.id === creditOutlet.id);
            let data;
            if(dataIndex === -1){
              data = this.initializeOutlet(creditOutlet);
            }else{
              data = outlets[dataIndex];
            }
            let {equity, is_calculated, is_manual} = this.outletGrossEquity(creditOutlet, credit);
            if(data) {
              let credit_data = {
                name: this.getCreditName(credit),
                ownership: creditOutlet.ownership,
                equity,
                is_calculated,
                is_manual,
              }
              data.credits.push(credit_data);
              if(credit_data.name.length) {
                if (dataIndex > -1) {
                  outlets[dataIndex] = data;
                } else {
                  outlets.push(data);
                }
              }
            }
          }
        }
      }
    }
    return outlets.sort((a: any, b: any) =>
      a.name > b.name ? 1 : b.name > a.name ? -1 : 0
    );
  }

  public commaCurrencyToInt(value: string) : number {
    if(typeof value){
      return parseFloat(value)
    }
    return parseFloat(value.replace(/,/g, ""))

  }
}
